
import { MessageThread, Message } from "./types";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger('threadUtils');

/**
 * Groups messages by thread for better organization and display
 */
export function groupMessagesByThread(messages: Message[]): Record<string, Message[]> {
  const threads: Record<string, Message[]> = {};
  
  messages.forEach(message => {
    // Create a thread key based on the participants and optionally reagent
    const participants = [message.sender_id, message.recipient_id].sort().join('-');
    const threadKey = message.reagent_id 
      ? `${participants}-${message.reagent_id}` 
      : participants;
    
    if (!threads[threadKey]) {
      threads[threadKey] = [];
    }
    
    threads[threadKey].push(message);
  });
  
  // Sort messages within each thread by date
  Object.keys(threads).forEach(key => {
    threads[key].sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  });
  
  return threads;
}

/**
 * Finds the most recent message in each thread
 */
export function getLatestMessages(threads: Record<string, Message[]>): Record<string, Message> {
  const latestMessages: Record<string, Message> = {};
  
  Object.keys(threads).forEach(key => {
    const messagesInThread = threads[key];
    if (messagesInThread.length > 0) {
      latestMessages[key] = messagesInThread[messagesInThread.length - 1];
    }
  });
  
  return latestMessages;
}

/**
 * Gets unread count for each thread for a specific user
 */
export function getUnreadCounts(
  threads: Record<string, Message[]>, 
  userId: string
): Record<string, number> {
  const unreadCounts: Record<string, number> = {};
  
  Object.keys(threads).forEach(key => {
    unreadCounts[key] = threads[key].filter(
      message => message.recipient_id === userId && !message.read
    ).length;
  });
  
  return unreadCounts;
}

/**
 * Converts a message thread object to a format suitable for the UI
 */
export function formatMessageThread(thread: MessageThread): {
  id: string;
  title: string;
  subtitle?: string;
  lastMessage?: string;
  timestamp: string;
  unreadCount: number;
} {
  return {
    id: `${thread.user_id}${thread.reagent_id ? `-${thread.reagent_id}` : ''}`,
    title: thread.username || 'Unknown User',
    subtitle: thread.reagent_name ? `Re: ${thread.reagent_name}` : undefined,
    lastMessage: thread.last_message,
    timestamp: thread.last_message_at,
    unreadCount: thread.unread_count
  };
}

/**
 * Utility to determine if two users are in a conversation about a reagent
 */
export function areUsersInConversation(
  user1Id: string,
  user2Id: string, 
  reagentId?: string
): boolean {
  // This would typically check the database - for now it's a simple function
  // that can be expanded later to actually check message history
  return user1Id !== user2Id; // Users can't message themselves
}
